import { create } from 'zustand'
import type { Exercise } from '../../api/types.ts'

export type Draft = {
	prompt?: string
	data?: unknown
	answer?: unknown
	explanation?: string
	/** Bumped on every keystroke. A save clears the draft only if it did not move meanwhile. */
	version: number
}

export type SaveState = 'clean' | 'dirty' | 'saving' | 'error'

type EditorState = {
	/** The activity these buffers belong to. Switching activities throws them away. */
	activityId: string | null
	/** Only fields she has actually typed into. Never a copy of what the server returned. */
	drafts: Record<string, Draft>
	saving: Record<string, true>
	errors: Record<string, SaveError>
	/** A save answered "no such exercise": it was deleted in another tab. Shown until dismissed. */
	deletedElsewhere: boolean
	/**
	 * She pressed Publish while an exercise was not ready. Until then a box that is merely unfinished
	 * stays quiet; after it every such box says what is wrong. Cleared once nothing is wrong.
	 */
	publishTried: boolean

	open: (activityId: string) => void
	edit: (exerciseId: string, patch: Omit<Draft, 'version'>) => void
	/** The version to hand back to `settle` once the request resolves. */
	beginSave: (exerciseId: string) => number
	settle: (exerciseId: string, version: number) => void
	/** `invalid`: the server refused what she typed (a blank box), not a failure to reach it. */
	fail: (exerciseId: string, message: string, invalid?: boolean) => void
	/** Forgets one exercise: its draft, its save in flight and its error. */
	discard: (exerciseId: string) => void
	/** Forgets everything unsaved, for "Leave anyway". */
	discardAll: () => void
	noteDeletedElsewhere: (value: boolean) => void
	setPublishTried: (value: boolean) => void
}

/** Why her last save did not land. */
export type SaveError = { message: string; invalid: boolean }

/**
 * UI state only: what she has typed and not yet saved, and how each save is going. The saved
 * exercises themselves live in TanStack Query. Nothing here is ever seeded from a query
 * result, which is what keeps a single source of truth for every field on the screen.
 */
export const useEditorStore = create<EditorState>((set, get) => ({
	activityId: null,
	drafts: {},
	saving: {},
	errors: {},
	deletedElsewhere: false,
	publishTried: false,

	open: (activityId) => {
		if (get().activityId === activityId) return
		set({
			activityId,
			drafts: {},
			saving: {},
			errors: {},
			deletedElsewhere: false,
			publishTried: false,
		})
	},

	edit: (exerciseId, patch) =>
		set((s) => {
			const prev = s.drafts[exerciseId]
			const { [exerciseId]: _dropped, ...errors } = s.errors
			return {
				drafts: {
					...s.drafts,
					[exerciseId]: { ...prev, ...patch, version: (prev?.version ?? 0) + 1 },
				},
				errors,
			}
		}),

	beginSave: (exerciseId) => {
		set((s) => ({ saving: { ...s.saving, [exerciseId]: true } }))
		return get().drafts[exerciseId]?.version ?? 0
	},

	settle: (exerciseId, version) =>
		set((s) => {
			const { [exerciseId]: _s, ...saving } = s.saving
			const { [exerciseId]: _e, ...errors } = s.errors
			// She kept typing while the request was in flight: keep the newer text.
			if (s.drafts[exerciseId] && s.drafts[exerciseId].version !== version) {
				return { saving, errors }
			}
			const { [exerciseId]: _d, ...drafts } = s.drafts
			return { drafts, saving, errors }
		}),

	// The draft survives on purpose: a failed save must never swallow her text.
	fail: (exerciseId, message, invalid = false) =>
		set((s) => {
			const { [exerciseId]: _s, ...saving } = s.saving
			return { saving, errors: { ...s.errors, [exerciseId]: { message, invalid } } }
		}),

	discard: (exerciseId) =>
		set((s) => {
			const { [exerciseId]: _d, ...drafts } = s.drafts
			const { [exerciseId]: _e, ...errors } = s.errors
			const { [exerciseId]: _s, ...saving } = s.saving
			return { drafts, errors, saving }
		}),

	discardAll: () => set({ drafts: {}, saving: {}, errors: {} }),

	noteDeletedElsewhere: (deletedElsewhere) => set({ deletedElsewhere }),

	setPublishTried: (publishTried) => set({ publishTried }),
}))

/** The saved row with her unsaved edits laid over it. One value on screen, derived. */
export const overlayDraft = (exercise: Exercise, draft: Draft | undefined): Exercise =>
	draft
		? ({
				...exercise,
				...(draft.prompt !== undefined ? { prompt: draft.prompt } : {}),
				...(draft.data !== undefined ? { data: draft.data } : {}),
				...(draft.answer !== undefined ? { answer: draft.answer } : {}),
				...(draft.explanation !== undefined ? { explanation: draft.explanation } : {}),
			} as Exercise)
		: exercise

/** Selectors. Each one narrows a card's subscription to the single row it renders. */
export const selectDraft = (id: string) => (s: EditorState) => s.drafts[id]
export const selectError = (id: string) => (s: EditorState) => s.errors[id]
export const selectSaveState =
	(id: string) =>
	(s: EditorState): SaveState =>
		// A refused blank is still her unsaved draft, not a broken save: it reads as unsaved.
		s.errors[id] && !s.errors[id].invalid
			? 'error'
			: s.saving[id]
				? 'saving'
				: s.drafts[id]
					? 'dirty'
					: 'clean'
export const selectPendingCount = (s: EditorState) =>
	Object.keys(s.drafts).length + Object.keys(s.saving).length
export const selectHasError = (s: EditorState) => Object.keys(s.errors).length > 0
/** A save that failed to reach the server; a refused blank box is her unsaved draft, not this. */
export const selectHasFailure = (s: EditorState) => Object.values(s.errors).some((e) => !e.invalid)
/** Work actually on its way: in flight, or typed and not yet refused. A refused blank waits for her. */
export const selectSavingCount = (s: EditorState) =>
	Object.keys(s.saving).length +
	Object.keys(s.drafts).filter((id) => !s.saving[id] && !s.errors[id]?.invalid).length
export const selectHasRefused = (s: EditorState) => Object.values(s.errors).some((e) => e.invalid)
